import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Lock, CheckCircle, ChevronRight, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const LearningPathsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPaths();
  }, []);

  const fetchPaths = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/learning/paths`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setPaths(data.paths || []);
    } catch (error) {
      toast.error('Erro ao carregar trilhas');
    } finally {
      setLoading(false);
    }
  };

  const handlePathClick = (path) => {
    if (!path.is_free && user?.subscription_plan === 'free') {
      toast.error('Esta trilha requer plano premium');
      return;
    }
    navigate(`/learning/${path.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="animate-pulse text-forest text-lg">Carregando trilhas...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand" data-testid="learning-paths-page">
      {/* Header */}
      <div className="bg-forest text-white p-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-semibold">Trilhas de Aprendizagem</h1>
            <p className="text-sm text-white/70">Aprenda de forma estruturada</p>
          </div>
        </div>
      </div>

      {/* Paths List */}
      <div className="p-4 space-y-4 max-w-lg mx-auto">
        {paths.map((path) => {
          const isPremiumLocked = !path.is_free && user?.subscription_plan === 'free';
          
          return (
            <div
              key={path.id}
              onClick={() => handlePathClick(path)}
              className={`bg-white rounded-2xl p-4 shadow-sm border border-forest/10 cursor-pointer 
                transition-all hover:shadow-md ${isPremiumLocked ? 'opacity-80' : ''}`}
              data-testid={`path-${path.id}`}
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl shrink-0"
                  style={{ backgroundColor: `${path.color}20` }}
                >
                  {path.icon}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-forest">{path.name}</h3>
                    {path.is_free ? (
                      <span className="px-2 py-0.5 bg-lime/30 text-forest text-xs rounded-full font-medium">
                        Grátis
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        Premium
                      </span>
                    )}
                  </div>
                  
                  <p className="text-sm text-stone mt-1">{path.description}</p>
                  
                  <div className="flex items-center gap-4 mt-3">
                    <span className="text-xs text-stone">
                      {path.modules_count} módulos • {path.total_lessons} lições
                    </span>
                  </div>

                  {/* Progress Bar */}
                  {path.completed_lessons > 0 && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-stone">Progresso</span>
                        <span className="text-forest font-medium">{path.progress_percent}%</span>
                      </div>
                      <div className="h-2 bg-sand rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500"
                          style={{ 
                            width: `${path.progress_percent}%`,
                            backgroundColor: path.color 
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Arrow or Lock */}
                <div className="shrink-0 self-center">
                  {isPremiumLocked ? (
                    <Lock className="w-5 h-5 text-stone" />
                  ) : path.progress_percent === 100 ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-stone" />
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Premium CTA */}
        {user?.subscription_plan === 'free' && (
          <div className="bg-gradient-to-r from-forest to-forest/80 rounded-2xl p-4 text-white">
            <div className="flex items-center gap-3">
              <Sparkles className="w-8 h-8" />
              <div>
                <h4 className="font-semibold">Desbloqueie todas as trilhas</h4>
                <p className="text-sm text-white/80">
                  Assine o Premium para acessar trilhas intermediárias e avançadas
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/settings')}
              className="w-full mt-3 py-2 bg-white text-forest rounded-xl font-medium hover:bg-lime transition-colors"
              data-testid="upgrade-cta"
            >
              Ver Planos
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LearningPathsPage;
